'use client';

import { useEffect, useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useCompletion } from '@ai-sdk/react';
import { useRouter } from 'next/navigation';
import { Loader2, MessageCircle, Sparkles, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import type { OutreachAction } from '@/lib/ai/agents/outreach-skills';
import { OutreachContextPanel } from '@/components/outreach/outreach-context-panel';
import { OutreachQuickActions } from '@/components/outreach/outreach-quick-actions';
import type { OutreachContextPreview, OutreachTone } from '@/lib/outreach/types';
import { cn } from '@/lib/utils';

type RoomOption = { id: string; name: string };
type MemberOption = {
  id: string;
  name: string;
  email: string;
  role: string;
  company: string;
};

export type OutreachComposeProps = {
  initialAccountId?: string | null;
  initialDealId?: string | null;
  initialRoomId?: string | null;
  initialRecipientId?: string | null;
  initialUserContext?: string;
  accountName?: string | null;
  dealName?: string | null;
  variant?: 'page' | 'modal';
  onSent?: () => void;
  /** Called before navigating to the room thread (e.g. close compose modal). */
  onViewThread?: () => void;
  /** Stage AI draft for the room chat input when viewing the thread. */
  onStageChatDraft?: (draft: {
    roomId: string;
    recipientId: string;
    message: string;
  }) => void;
};

export function OutreachCompose({
  initialAccountId = null,
  initialDealId = null,
  initialRoomId = null,
  initialRecipientId = null,
  initialUserContext = '',
  accountName = null,
  dealName = null,
  variant = 'page',
  onSent,
  onViewThread,
  onStageChatDraft,
}: OutreachComposeProps) {
  const { data: session } = useSession();
  const { toast } = useToast();
  const router = useRouter();

  const [rooms, setRooms] = useState<RoomOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);

  const [roomId, setRoomId] = useState(initialRoomId ?? '');
  const [recipientId, setRecipientId] = useState(initialRecipientId ?? '');
  const [userContext, setUserContext] = useState(initialUserContext);
  const [tone, setTone] = useState<OutreachTone>('professional');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [contextPreview, setContextPreview] = useState<OutreachContextPreview | null>(null);
  const [contextLoading, setContextLoading] = useState(false);

  const { completion, complete, isLoading: aiLoading, setCompletion } = useCompletion({
    api: '/api/ai/outreach-assist',
  });

  useEffect(() => {
    if (completion) {
      setMessage(completion);
    }
  }, [completion]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRoomsLoading(true);
      try {
        const params = new URLSearchParams();
        if (initialAccountId) params.set('accountId', initialAccountId);
        const res = await fetch(`/api/rooms?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to load rooms');
        const data = (await res.json()) as Array<{ id: string; name: string }>;
        if (!cancelled) {
          const options = data.map((room) => ({ id: room.id, name: room.name }));
          setRooms(options);
          if (
            options.length === 1 &&
            (!roomId || !options.some((room) => room.id === roomId))
          ) {
            setRoomId(options[0].id);
          }
        }
      } catch {
        if (!cancelled) {
          toast({
            title: 'Could not load rooms',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setRoomsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialAccountId, toast]);

  useEffect(() => {
    if (!roomId) {
      setMembers([]);
      setRecipientId('');
      return;
    }

    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      try {
        const res = await fetch(`/api/rooms/${roomId}/members`);
        if (!res.ok) throw new Error('Failed to load members');
        const data = (await res.json()) as MemberOption[];
        if (!cancelled) {
          const filtered = data.filter((member) => member.id !== session?.user?.id);
          setMembers(filtered);
          if (initialRecipientId && filtered.some((m) => m.id === initialRecipientId)) {
            setRecipientId(initialRecipientId);
          } else if (recipientId && !filtered.some((m) => m.id === recipientId)) {
            setRecipientId('');
          }
        }
      } catch {
        if (!cancelled) {
          toast({
            title: 'Could not load room members',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recipientId intentionally excluded
  }, [roomId, session?.user?.id, initialRecipientId, toast]);

  useEffect(() => {
    if (!roomId || !recipientId) {
      setContextPreview(null);
      setContextLoading(false);
      return;
    }

    let cancelled = false;
    const loadContextPreview = async () => {
      setContextLoading(true);
      try {
        const params = new URLSearchParams({
          roomId,
          recipientId,
        });
        if (initialAccountId) params.set('accountId', initialAccountId);
        if (initialDealId) params.set('dealId', initialDealId);

        const res = await fetch(`/api/outreach/context?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to load context');
        const data = await res.json();
        if (!cancelled) {
          setContextPreview(data.preview as OutreachContextPreview);
        }
      } catch {
        if (!cancelled) {
          setContextPreview(null);
        }
      } finally {
        if (!cancelled) {
          setContextLoading(false);
        }
      }
    };

    void loadContextPreview();
    return () => {
      cancelled = true;
    };
  }, [roomId, recipientId, initialAccountId, initialDealId]);

  const selectedMember = useMemo(
    () => members.find((member) => member.id === recipientId) ?? null,
    [members, recipientId]
  );

  const runAiAction = async (
    action: OutreachAction,
    refineTone?: OutreachTone,
    redraft = false
  ) => {
    if (!roomId || !recipientId) return;

    const hasDraft = Boolean(message.trim());

    if (action !== 'draft' && !hasDraft) {
      toast({
        title: 'Add a message first',
        description: 'Draft a message before using refine options.',
        variant: 'destructive',
      });
      return;
    }

    let effectiveAction = action;
    if (action === 'draft' && hasDraft && !redraft) {
      effectiveAction = 'improve';
    }

    setCompletion('');
    await complete('', {
      body: {
        action: effectiveAction,
        roomId,
        recipientId,
        userContext,
        tone: refineTone ?? tone,
        accountId: initialAccountId,
        dealId: initialDealId,
        currentDraft:
          effectiveAction === 'draft' ? undefined : message.trim() || undefined,
      },
    });
  };

  const handleSend = async () => {
    if (!roomId || !recipientId || !message.trim()) return;

    setSending(true);
    try {
      const res = await fetch('/api/chat/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: message.trim(),
          roomId,
          visibility: 'PRIVATE',
          recipientId,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to send message');
      }

      toast({
        title: 'Message sent',
        description: `Private message delivered to ${selectedMember?.name ?? 'member'}.`,
      });
      if (onSent) {
        onSent();
      } else {
        router.push(`/room/${roomId}?chat=open&userId=${recipientId}`);
      }
    } catch (error) {
      toast({
        title: 'Send failed',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleViewThread = () => {
    if (!roomId || !recipientId) return;
    onStageChatDraft?.({
      roomId,
      recipientId,
      message: message.trim(),
    });
    onViewThread?.();
    router.push(`/room/${roomId}?chat=open&userId=${recipientId}`);
  };

  if (session?.user?.userType === 'EXTERNAL') {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Private outreach is available to internal team members only.
      </div>
    );
  }

  return (
    <div
      className={cn(
        'space-y-6',
        variant === 'page' ? 'max-w-4xl mx-auto p-6' : 'p-6 pb-8'
      )}
    >
      {(accountName || dealName) && (
        <div className="text-sm text-muted-foreground">
          {accountName && <span>Account: {accountName}</span>}
          {accountName && dealName && <span className="mx-2">·</span>}
          {dealName && <span>Deal: {dealName}</span>}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Room</Label>
          <Select
            value={roomId}
            onValueChange={(value) => {
              setRoomId(value);
              setRecipientId('');
            }}
            disabled={roomsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder={roomsLoading ? 'Loading rooms…' : 'Select a room'} />
            </SelectTrigger>
            <SelectContent>
              {rooms.map((room) => (
                <SelectItem key={room.id} value={room.id}>
                  {room.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>To</Label>
          <Select
            value={recipientId}
            onValueChange={setRecipientId}
            disabled={!roomId || membersLoading}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  !roomId
                    ? 'Select a room first'
                    : membersLoading
                      ? 'Loading members…'
                      : 'Select a member'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {members.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  <span>{member.name}</span>
                  {member.role ? (
                    <span className="text-muted-foreground"> · {member.role}</span>
                  ) : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedMember && (
        <p className="text-sm text-muted-foreground">
          Messaging {selectedMember.name}
          {selectedMember.company ? ` at ${selectedMember.company}` : ''}
        </p>
      )}

      <OutreachContextPanel
        preview={contextPreview}
        loading={contextLoading && !contextPreview}
        recipientName={selectedMember?.name}
        accountId={initialAccountId}
        dealId={initialDealId}
      />

      <div className="space-y-2">
        <Label htmlFor="outreach-context">Your context (helps AI draft)</Label>
        <Textarea
          id="outreach-context"
          value={userContext}
          onChange={(e) => setUserContext(e.target.value)}
          placeholder="e.g. They asked about SSO last week. We just added the security doc to the room."
          className="min-h-[100px]"
        />
      </div>

      <div className="space-y-2">
        <Label>Default tone for new drafts</Label>
        <Select value={tone} onValueChange={(value) => setTone(value as OutreachTone)}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="friendly">Friendly</SelectItem>
            <SelectItem value="concise">Concise</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="outreach-message">Message</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void runAiAction('draft')}
            disabled={!roomId || !recipientId || aiLoading}
          >
            {aiLoading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-1" />
            )}
            {message.trim() ? 'Improve with AI' : 'Draft with AI'}
          </Button>
        </div>
        <Textarea
          id="outreach-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Write your message or use AI to draft one…"
          className="min-h-[160px]"
        />
        {message.trim() ? (
          <OutreachQuickActions
            disabled={!roomId || !recipientId}
            loading={aiLoading}
            onAction={(action, refineTone, redraft) =>
              void runAiAction(action, refineTone, redraft)
            }
          />
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
        <p className="text-xs text-muted-foreground">
          Sends as a private room chat message. AI can make mistakes — review before sending.
        </p>
        <div className="flex gap-2">
          {roomId && recipientId && (
            <Button type="button" variant="outline" onClick={handleViewThread}>
              <MessageCircle className="h-4 w-4 mr-1" />
              View thread
            </Button>
          )}
          <Button
            onClick={() => void handleSend()}
            disabled={!roomId || !recipientId || !message.trim() || sending || aiLoading}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1" />
            )}
            Send message
          </Button>
        </div>
      </div>
    </div>
  );
}
